import React from 'react';
import useDataFetching from '../../hooks/useDataFetching';
import LoadingState from '../shared/LoadingState';
import ErrorState from '../shared/ErrorState';
import DashboardStats from './DashboardStats';
import CostSavingsCard from './CostSavingsCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// API function
const getDashboardStats = async () => {
  const response = await fetch(`${BACKEND_URL}/api/dashboard/stats`);
  if (!response.ok) {
    throw new Error('Failed to fetch dashboard stats');
  }
  return response.json();
};

const generateCostSavingsMetrics = (stats) => {
  const resolvedAnomalies = stats?.resolved_anomalies || 12;
  const avgIncidentCost = 15000;
  const preventedDowntime = resolvedAnomalies * 2.5;
  const downtimeHourlyCost = 8500;
  
  return {
    monthlySavings: resolvedAnomalies * avgIncidentCost * 0.7,
    downtimePrevention: preventedDowntime * downtimeHourlyCost,
    mttrReduction: 75,
    earlyDetection: 89,
    monthlyROI: 340,
    annualSavings: resolvedAnomalies * avgIncidentCost * 0.7 * 12,
  };
};

const Dashboard = () => {
  // Fetch dashboard stats (NO auto-refetch)
  const { 
    data: stats, 
    loading, 
    error,
    refetch
  } = useDataFetching(getDashboardStats, []);

  if (loading && !stats) {
    return <LoadingState message="Loading dashboard..." fullScreen />;
  }

  if (error && !stats) {
    return <ErrorState message={error} onRetry={refetch} fullScreen />;
  }

  const costSavings = generateCostSavingsMetrics(stats);

  return (
    <div className="min-h-full bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Juno HawkAI Dashboard
            </h1>
            <p className="text-gray-600 mt-1">Minimal Performance Mode</p>
          </div>
          <div className="flex items-center gap-2 px-4 py-2 bg-green-50 text-green-700 border border-green-200 rounded-lg">
            <span className="text-sm font-medium">✓ System Operational</span>
          </div>
        </div>

        {/* Main Stats */}
        <DashboardStats stats={stats} />

        {/* Cost Savings */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <CostSavingsCard costSavings={costSavings} />
          
          {/* Info Card */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-blue-900 mb-2">
              🚀 Minimal Dashboard Mode
            </h3>
            <p className="text-sm text-blue-800 mb-3">
              This version loads only essential stats to prevent system overload.
            </p>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• Single API call on load</li>
              <li>• No auto-refresh</li>
              <li>• No WebSocket streams</li>
              <li>• Optimized for performance</li>
            </ul>
            <button
              onClick={refetch}
              className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm"
            >
              ↻ Refresh Data
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
